import 'draft-js/dist/Draft.css';
import { useRef, useState, useEffect, useCallback } from 'react';
import { EditorState } from 'draft-js';
import escapeHtml from '../escapeHtml';
import { stateFromHTML } from 'draft-js-import-html';
import { stateToHTML } from 'draft-js-export-html';
import EditorWrapper from './EditorWrapper';
import EditorToolbar from './EditorToolbar';
import EditorContentWrapper from './EditorContentWrapper';
import InnerEditor from './InnerEditor';
import DisabledContext from './DisabledContext';
import type PluginEditor from '@draft-js-plugins/editor/lib/Editor';
import useBlockPortfolioItemCloseWhenDirty from '@/components/ChallengePortfolio/NormalView/portfolio-item/useBlockPortfolioItemCloseWhenDirty';

type TextEditorProps =
  | {
      initialValue?: string;
      onCancel?: () => void;
      onSave: (value: string | null) => Promise<void>;
      disableSaveWhenEmpty?: boolean;
      disabled?: boolean;
    }
  | {
      initialValue?: string;
      onCancel?: () => void;
      onSave: (value: string) => Promise<void>;
      disableSaveWhenEmpty: true;
      disabled?: boolean;
    };

// TODO actually show saving indicator in save icon button
// TODO seemingly, shortcuts like Cmd+B, Cmd+I are broken
const TextEditor = ({
  initialValue,
  onCancel,
  onSave,
  disableSaveWhenEmpty,
  disabled,
}: TextEditorProps) => {
  const editorRef = useRef<PluginEditor>(null);

  const initialEditorState = !initialValue
    ? EditorState.createEmpty()
    : EditorState.createWithContent(
        stateFromHTML(escapeHtml(initialValue ?? '')),
      );

  const [editorState, setEditorState] = useState(initialEditorState);

  const editorContent = editorState.getCurrentContent();
  const editorIsEmpty = !editorContent.hasText();

  const focusOnEditor = (): void => {
    // Without this timeout, the link detection plugin breaks
    setTimeout(() => {
      if (editorRef.current !== null) {
        editorRef.current.focus();
      }
    }, 100);
  };

  useEffect(focusOnEditor, []);

  const handleSave = useCallback(async (): Promise<void> => {
    if (disableSaveWhenEmpty && editorIsEmpty) {
      return;
    }

    if (editorIsEmpty) {
      await onSave(null as unknown as string);

      return;
    }

    await onSave(stateToHTML(editorContent));
  }, [disableSaveWhenEmpty, editorIsEmpty, onSave, editorContent]);

  useBlockPortfolioItemCloseWhenDirty(true, handleSave);

  return (
    <DisabledContext.Provider value={disabled ?? false}>
      <EditorWrapper className="text-editor">
        <EditorToolbar
          editorState={editorState}
          setEditorState={setEditorState}
          onCancel={onCancel}
          onSave={handleSave}
          focusOnEditor={focusOnEditor}
          saveDisabled={(disableSaveWhenEmpty && editorIsEmpty) ?? false}
        />
        <EditorContentWrapper>
          <InnerEditor
            ref={editorRef}
            editorState={editorState}
            onChange={editorState => setEditorState(editorState)}
            onEnterSave={handleSave}
            readOnly={disabled}
          />
        </EditorContentWrapper>
      </EditorWrapper>
    </DisabledContext.Provider>
  );
};

export default TextEditor;
